import React, { useState } from 'react';
import { Send, PhoneCall, Mail, MapPin, Loader2, MessageCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { submitEnquiry } from '../lib/api';
import { CONTACT } from '../mock/mock';

const Input = ({ label, name, type = 'text', required, value, onChange, placeholder }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-[#7A5C4C] mb-2">{label}{required && ' *'}</label>
    <input type={type} name={name} required={required} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full bg-white border border-[#E4D6C1] rounded-lg px-4 py-3 text-sm text-[#2C2416] placeholder:text-[#B9A995] focus:outline-none focus:border-[#C58B76] focus:ring-2 focus:ring-[#F4D5C5] transition-colors" />
  </div>
);

const ContactForm = ({ source = 'website' }) => {
  const [form, setForm] = useState({ name: '', phone: '', company: '', email: '', budget: '', quantity: '', message: '' });
  const [loading, setLoading] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitEnquiry({ ...form, source });
      toast({ title: 'Enquiry received', description: `Thanks ${form.name}! Our gifting stylist will reach out within 24 hours.` });
      setForm({ name: '', phone: '', company: '', email: '', budget: '', quantity: '', message: '' });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: err?.response?.data?.detail || 'Please try again or WhatsApp us.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquire" className="py-16 md:py-24 bg-[#F4E7DA]">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3">Get in touch</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#2C2416] leading-tight">Let's craft something special together</h2>
          <p className="text-[#5B4A3A] mt-4 max-w-md leading-relaxed">Share your gifting brief and we will curate hamper options within your budget - typically within 24 hours.</p>
          <ul className="mt-8 space-y-4 text-sm">
            <li className="flex items-start gap-3"><PhoneCall className="w-4 h-4 mt-1 text-[#C58B76]" /><div><div className="font-medium text-[#2C2416]">Call us</div><a href={`tel:${CONTACT.phoneRaw}`} className="text-[#5B4A3A] hover:text-[#C58B76]">{CONTACT.phone}</a></div></li>
            <li className="flex items-start gap-3"><MessageCircle className="w-4 h-4 mt-1 text-[#C58B76]" /><div><div className="font-medium text-[#2C2416]">WhatsApp</div><a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="text-[#5B4A3A] hover:text-[#C58B76]">Chat with a stylist</a></div></li>
            <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-1 text-[#C58B76]" /><div><div className="font-medium text-[#2C2416]">Email</div><a href={`mailto:${CONTACT.email}`} className="text-[#5B4A3A] hover:text-[#C58B76]">{CONTACT.email}</a></div></li>
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-1 text-[#C58B76]" /><div><div className="font-medium text-[#2C2416]">Studio</div><div className="text-[#5B4A3A]">{CONTACT.address}</div></div></li>
          </ul>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-3 bg-white rounded-2xl p-6 md:p-10 border border-[#E4D6C1]">
          <h3 className="font-serif-display text-2xl text-[#2C2416] mb-6">Corporate Gifting Enquiry</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Name" name="name" required value={form.name} onChange={onChange} placeholder="Your name" />
            <Input label="Phone Number" name="phone" type="tel" required value={form.phone} onChange={onChange} placeholder="+91" />
            <Input label="Company Name" name="company" value={form.company} onChange={onChange} placeholder="Company (optional)" />
            <Input label="Email ID" name="email" type="email" required value={form.email} onChange={onChange} placeholder="you@work.com" />
            <Input label="Approx. Budget (per Hamper)" name="budget" value={form.budget} onChange={onChange} placeholder="₹ 1,000 - ₹ 5,000" />
            <Input label="Approx. No. of Gifts" name="quantity" value={form.quantity} onChange={onChange} placeholder="e.g. 100" />
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest text-[#7A5C4C] mb-2">Message</label>
              <textarea name="message" rows="4" value={form.message} onChange={onChange} placeholder="Tell us about the occasion, timeline and preferences"
                className="w-full bg-white border border-[#E4D6C1] rounded-lg px-4 py-3 text-sm text-[#2C2416] placeholder:text-[#B9A995] focus:outline-none focus:border-[#C58B76] focus:ring-2 focus:ring-[#F4D5C5] transition-colors" />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-8 py-3 mt-6 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : <>Send Enquiry <Send className="w-4 h-4" /></>}
          </button>
          <p className="text-xs text-[#7A5C4C] mt-4">Our team gets back within 24 hours to begin crafting your perfect gifting experience.</p>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
